/**
 * Vista Libro Diario de FinVoz (FR-LEDGER-1..7, FR-IMPORT-1..4).
 *
 * Muestra:
 *  - Lista cronológica de asientos inmutables (INV-3)
 *  - Columnas completas: Fecha, Descripción, Importe, Moneda, Debe, Haber y Origen
 *  - Búsqueda y filtros por fecha (desde/hasta), cuenta, moneda y texto (FR-LEDGER-6)
 *  - Acción de ver detalle completo y reversión inmutable referenciada (FR-LEDGER-3)
 *  - Garantía INV-3: No se permite editar ni borrar asientos confirmados; las correcciones
 *    se realizan mediante reversión + asiento nuevo.
 *  - No relabelar importes históricos: cada asiento conserva su moneda original.
 *  - Exportación e importación delimitada (CSV) (FR-LEDGER-7, FR-IMPORT-1..4)
 */

import type { AppState } from "../types.js";
import { formatMinorUnits, formatDate } from "../format.js";
import { escapeHtml, escapeAttr } from "../sanitize.js";

export function renderLedger(state: AppState): string {
  // Mapa de nombres de cuenta para presentación
  const accountNames = new Map(state.accounts.map((a) => [a.accountId, a.name]));

  // Identificar qué asientos han sido revertidos por algún otro asiento
  const reversedEntryIds = new Set(
    state.entries
      .map((c) => c.entry.reversesEntryId)
      .filter((id): id is string => Boolean(id)),
  );

  return `
    <div class="ledger-view" data-testid="ledger-view">
      <!-- Barra de Acciones y Filtros -->
      <div class="card" style="margin-bottom: 16px;">
        <div class="card-header" style="margin-bottom: 12px; flex-wrap: wrap; gap: 8px;">
          <div>
            <h2 class="card-title">Libro Diario (${state.entries.length} asientos)</h2>
            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 2px;">
              Histórico inmutable append-only (INV-3). Las correcciones requieren reversión y nuevo asiento.
            </div>
          </div>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <button class="btn-secondary" data-testid="export-csv-btn" data-action="export-csv">
              📥 Exportar CSV
            </button>
            <button class="btn-secondary" data-testid="import-csv-btn" data-action="open-import-modal">
              📤 Importar Extracto
            </button>
          </div>
        </div>

        <!-- Filtros Completos: Texto, Cuenta, Moneda y Rango de Fechas (FR-LEDGER-6) -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px;">
          <div>
            <label for="filter-text" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Buscar texto:</label>
            <input
              type="text"
              id="filter-text"
              data-testid="filter-text"
              placeholder="Descripción o ID..."
              style="width: 100%;"
            />
          </div>
          <div>
            <label for="filter-account" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Filtrar por cuenta:</label>
            <select id="filter-account" data-testid="filter-account" style="width: 100%;">
              <option value="">Todas las cuentas</option>
              ${state.accounts.map((a) => `<option value="${escapeAttr(a.accountId)}">${escapeHtml(a.name)}</option>`).join("")}
            </select>
          </div>
          <div>
            <label for="filter-currency" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Filtrar por moneda:</label>
            <select id="filter-currency" data-testid="filter-currency" style="width: 100%;">
              <option value="">Todas las monedas</option>
              <option value="PEN">PEN (S/)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
          <div>
            <label for="filter-start-date" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Desde fecha:</label>
            <input type="date" id="filter-start-date" data-testid="filter-start-date" style="width: 100%;" />
          </div>
          <div>
            <label for="filter-end-date" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Hasta fecha:</label>
            <input type="date" id="filter-end-date" data-testid="filter-end-date" style="width: 100%;" />
          </div>
        </div>
      </div>

      <!-- Tabla de Asientos con Columnas Requeridas -->
      <div class="card" style="padding: 0; overflow: hidden;">
        <div class="table-container">
          <table data-testid="ledger-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Descripción</th>
                <th>Importe</th>
                <th>Moneda</th>
                <th>Debe</th>
                <th>Haber</th>
                <th>Origen</th>
                <th style="text-align: right;">Acciones</th>
              </tr>
            </thead>
            <tbody id="ledger-tbody">
              ${state.entries.length === 0
                ? `<tr><td colspan="8" style="text-align: center; color: var(--text-muted); padding: 32px;">No hay asientos en el libro. Registra uno en Captura.</td></tr>`
                : state.entries
                    .slice()
                    .reverse()
                    .map((chained) => {
                      const e = chained.entry;
                      const isReversal = Boolean(e.reversesEntryId);
                      const isAlreadyReversed = reversedEntryIds.has(e.entryId);

                      // Apuntes Debe (positivos) y Haber (negativos)
                      const debits = e.postings.filter((p) => p.amountMinor > 0n);
                      const credits = e.postings.filter((p) => p.amountMinor < 0n);

                      // Total importe principal del asiento
                      const totalDebit = debits.reduce((sum, p) => sum + p.amountMinor, 0n);

                      // Moneda propia del asiento (cada asiento conserva su moneda original)
                      const entryCurrency = e.postings[0]?.currency || "PEN";
                      const entryScale = e.postings[0]?.scale || 2;

                      return `
                        <tr
                          data-entry-id="${escapeAttr(e.entryId)}"
                          data-currency="${escapeAttr(entryCurrency)}"
                          data-testid="entry-row-${escapeAttr(e.entryId)}"
                        >
                          <td style="white-space: nowrap; font-family: var(--font-mono); font-size: 0.85rem;">
                            ${formatDate(e.date)}
                          </td>
                          <td>
                            <div style="font-weight: 500;">${escapeHtml(e.description)}</div>
                            <div style="font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono); margin-top: 2px;">
                              ID: ${escapeHtml(e.entryId.slice(0, 16))}... • #${chained.deviceSeq}
                            </div>
                          </td>
                          <td style="font-family: var(--font-mono); font-weight: 600; white-space: nowrap;">
                            ${formatMinorUnits(totalDebit, entryCurrency, entryScale)}
                          </td>
                          <td>
                            <span class="badge" style="font-family: var(--font-mono); font-weight: 600; background-color: var(--bg-tertiary); color: var(--text-primary);">
                              ${escapeHtml(entryCurrency)}
                            </span>
                          </td>
                          <td style="font-size: 0.8rem;">
                            ${debits.map((p) => {
                              const accName = accountNames.get(p.accountId) ?? p.accountId;
                              return `<div style="margin-bottom: 2px;">
                                <span style="font-weight: 500;">${escapeHtml(accName)}</span>
                                <span style="font-family: var(--font-mono); color: var(--text-secondary);">(${formatMinorUnits(p.amountMinor, p.currency, p.scale)})</span>
                              </div>`;
                            }).join("")}
                          </td>
                          <td style="font-size: 0.8rem;">
                            ${credits.map((p) => {
                              const accName = accountNames.get(p.accountId) ?? p.accountId;
                              const positiveAmount = -p.amountMinor;
                              return `<div style="margin-bottom: 2px;">
                                <span style="font-weight: 500;">${escapeHtml(accName)}</span>
                                <span style="font-family: var(--font-mono); color: var(--text-secondary);">(${formatMinorUnits(positiveAmount, p.currency, p.scale)})</span>
                              </div>`;
                            }).join("")}
                          </td>
                          <td>
                            <span class="badge badge-${escapeAttr(e.origin)}">${escapeHtml(e.origin)}</span>
                            ${isReversal ? `<span class="badge" style="background-color: var(--danger-bg); color: var(--danger); margin-top: 2px; display: inline-block;">Reversión</span>` : ""}
                            ${isAlreadyReversed ? `<span class="badge" style="background-color: var(--warning-bg); color: var(--warning-text); margin-top: 2px; display: inline-block;">Revertido</span>` : ""}
                          </td>
                          <td style="text-align: right; white-space: nowrap;">
                            <div style="display: inline-flex; gap: 4px; align-items: center;">
                              <button
                                class="btn-secondary"
                                style="font-size: 0.75rem; padding: 4px 8px;"
                                title="Ver detalle completo del asiento"
                                data-testid="view-detail-${escapeAttr(e.entryId)}"
                                data-action="view-entry-detail"
                                data-entry-id="${escapeAttr(e.entryId)}"
                              >
                                👁️ Detalle
                              </button>
                              ${!isReversal && !isAlreadyReversed
                                ? `<button
                                    class="btn-secondary"
                                    style="font-size: 0.75rem; padding: 4px 8px; color: var(--danger);"
                                    title="Crear asiento de reversión (INV-3)"
                                    data-testid="reverse-entry-${escapeAttr(e.entryId)}"
                                    data-action="reverse-entry"
                                    data-entry-id="${escapeAttr(e.entryId)}"
                                  >
                                    ↺ Revertir
                                  </button>`
                                : isAlreadyReversed
                                ? `<span style="font-size: 0.75rem; color: var(--text-muted); padding: 4px;">Revertido</span>`
                                : `<span style="font-size: 0.75rem; color: var(--text-muted); padding: 4px;">—</span>`
                              }
                            </div>
                          </td>
                        </tr>
                      `;
                    }).join("")}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal de Detalle de Asiento (INV-3, FR-LEDGER-1..5) -->
      <div id="entry-detail-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="detail-modal-title">
        <div class="modal-content" style="max-width: 650px;">
          <div class="card-header">
            <h3 class="card-title" id="detail-modal-title">Detalle de Asiento Contable</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-entry-detail-modal" aria-label="Cerrar detalle">&times;</button>
          </div>
          <div id="entry-detail-body" style="margin-top: 12px;">
            <!-- Renderizado dinámico desde app.ts -->
          </div>
          <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px;">
            <div id="detail-modal-actions" style="display: inline-flex; gap: 8px;"></div>
            <button class="btn-secondary" data-action="close-entry-detail-modal">Cerrar</button>
          </div>
        </div>
      </div>

      <!-- Modal de Importación CSV (FR-IMPORT-1..4) -->
      <div id="import-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="import-modal-title">
        <div class="modal-content">
          <div class="card-header">
            <h3 class="card-title" id="import-modal-title">Importar Extracto Delimitado (CSV)</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-import-modal" aria-label="Cerrar importación">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 16px;">
            Selecciona un archivo CSV delimitado. El sistema propondrá asientos para confirmación explícita (FR-IMPORT-2) y deduplicará filas existentes (FR-IMPORT-3).
          </p>

          <input type="file" id="import-file-input" accept=".csv,text/csv" style="width: 100%; margin-bottom: 16px;" />

          <div id="import-preview-area" style="display: none; margin-bottom: 16px;">
            <div style="font-size: 0.85rem; font-weight: 600; margin-bottom: 6px;">Previsualización de Asientos a Importar:</div>
            <div id="import-preview-list" style="max-height: 180px; overflow-y: auto; background-color: var(--bg-tertiary); padding: 8px; border-radius: 6px; font-size: 0.8rem;"></div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 8px;">
            <button class="btn-secondary" data-action="close-import-modal">Cancelar</button>
            <button id="btn-confirm-import" class="btn-primary" style="display: none;" data-action="execute-import">
              Confirmar Importación
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}
